import classNames from 'classnames';
import { isUndefined } from 'lodash';
import * as React from 'react';

import { Bem } from '../../../service/Bem/Bem';
import { NavLinkData } from '../../../typings/common';
import { HoverTooltipWrapper } from '../../HoverTooltipWrapper/HoverTooltipWrapper';
import { RouteStyledLink, StyledLink, StyledLinkTheme } from '../../StyledLink/StyledLink';
import { TabMenuSize, TabMenuTheme } from '../TabMenu';

import './TabMenuLink.css';

export interface TabMenuLinkProps extends NavLinkData {
    hint?: string;
    tooltip?: string;
    size?: TabMenuSize;
    theme?: TabMenuTheme;
    onClick?: () => void;
    onMount?: () => void;
}

export class TabMenuLink extends React.PureComponent<TabMenuLinkProps> {
    private static getNavLinkTitleContent(title: React.ReactNode | React.ReactNode[]) {
        return <span className={Bem.cls('tab-menu-link', 'title')}>{title}</span>;
    }

    private static getNavLinkHintContent(hint?: string) {
        if (!hint) {
            return null;
        }

        return <span className={Bem.cls('tab-menu-link', 'hint')}>{hint}</span>;
    }

    constructor(props: TabMenuLinkProps) {
        super(props);

        this.state = {
            isTooltipVisible: false
        };
    }

    componentDidMount() {
        const { onMount } = this.props;

        if (!isUndefined(onMount)) {
            onMount();
        }
    }

    render() {
        const { tooltip, location } = this.props;

        const content = location ? this.getLocationTabLink() : this.getDisabledTabLink();

        if (tooltip) {
            return (
                <HoverTooltipWrapper
                    theme={'white'}
                    view={'classic'}
                    size={'n'}
                    to={['bottom', 'top']}
                    tooltipText={tooltip}
                    offset={0}
                >
                    {content}
                </HoverTooltipWrapper>
            );
        }

        return content;
    }

    private getLinkTheme = () => {
        const { theme = TabMenuTheme.common } = this.props;
        const dict: Record<TabMenuTheme, StyledLinkTheme> = {
            [TabMenuTheme.common]: StyledLinkTheme.common,
            [TabMenuTheme.grey]: StyledLinkTheme.grey
        };

        return dict[theme];
    };

    private getLocationTabLink() {
        const { target, isActive, isExternal, size = TabMenuSize.s, location, onClick } = this.props;
        const isLinkActive = Boolean(isActive);

        if (!location) {
            throw new Error('Location is undefined');
        }

        return (
            <span
                className={Bem.cls({
                    block: 'tab-menu-link',
                    mods: { size: size }
                })}
            >
                {isExternal ? (
                    <StyledLink
                        theme={this.getLinkTheme()}
                        cls={classNames(
                            Bem.cls({
                                block: 'tab-menu-link',
                                elem: 'link',
                                mods: {
                                    active: isLinkActive
                                }
                            })
                        )}
                        {...{
                            target,
                            href: location.toString(),
                            isLinkActive,
                            onClick
                        }}
                    >
                        {this.getLinkContent()}
                    </StyledLink>
                ) : (
                    <RouteStyledLink
                        theme={this.getLinkTheme()}
                        cls={classNames(
                            Bem.cls({
                                block: 'tab-menu-link',
                                elem: 'link',
                                mods: {
                                    active: isLinkActive
                                }
                            })
                        )}
                        {...{ target, href: location, isLinkActive, onClick }}
                    >
                        {this.getLinkContent()}
                    </RouteStyledLink>
                )}
            </span>
        );
    }

    private getDisabledTabLink() {
        const { size = TabMenuSize.s } = this.props;

        return (
            <span
                className={Bem.cls({
                    block: 'tab-menu-link',
                    mods: { disabled: true, size: size }
                })}
            >
                {this.getLinkContent()}
            </span>
        );
    }

    private getLinkContent() {
        const { hint, title } = this.props;

        return (
            <>
                {TabMenuLink.getNavLinkTitleContent(title)}

                {TabMenuLink.getNavLinkHintContent(hint)}
            </>
        );
    }
}
